import { useMemo, useState } from 'react';
import { Box, Button, Grid, makeStyles } from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { GridColDef, GridToolbar } from '@mui/x-data-grid';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import theme from 'App/theme';
import { useListSessions } from 'client/hooks/sessions/useListSessions';
import { useListUsers } from 'client/hooks/user/useListUsers';
import { useUser } from 'client/hooks/user/useUser';
import { RequireAdmin } from 'common/components/Auth/RequireAdmin';
import DataGrid from 'common/components/DataGrid/DataGrid';
import Panel from 'common/components/UI/Panel';
import { UserCognitoInfo } from 'common/types';

const styles = {
    root: {
        margin: theme.spacing(2),
        paddingTop: theme.spacing(2),
    },
    cellBorder: {
        margin: theme.spacing(2),
        //paddingTop: theme.spacing(2),
        '& .MuiDataGrid-columnHeader': {
            borderTop: '2px solid #f0f0f0',
        },
        '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
            borderRight: '1px solid #f0f0f0',
        },
    },
};

function convertMinutesToHoursAndMinutes(minutes: number): string {
    if (minutes < 0) {
        return '0m';
    }

    if (minutes < 60) {
        return `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round((minutes % 60) * 10) / 10; // Round to one decimal place

    return `${hours}h ${remainingMinutes}m`;
}

function filterSessionsByDate(sessions: any[], startDateFilter: any, endDateFilter: any): any[] {
    if (!sessions) {
        return [];
    }

    let startDate = new Date(0); // Default to epoch start

    try {
        if (startDateFilter) {
            const convertedDate = startDateFilter.toDate();

            if (!isNaN(convertedDate.getTime())) {
                startDate = startDateFilter.toDate();
            }
        }
    } catch (error) {
        console.error('Invalid start date filter:', error);
    }

    let endDate = new Date(); // Default to now

    try {
        if (endDateFilter) {
            const convertedDate = endDateFilter.toDate();

            if (!isNaN(convertedDate.getTime())) {
                endDate = endDateFilter.toDate();
            }
        }
    } catch (error) {
        console.error('Invalid end date filter:', error);
    }

    endDate.setDate(endDate.getDate() + 1); // Include the end date in the filter

    // @ts-ignore-next-line
    const filtered = sessions.filter(session => {
        const sessionDate = new Date(session.startTime);
        return sessionDate >= startDate && sessionDate <= endDate;
    });

    return filtered;
}

function filterAndSortedUsers(users: UserCognitoInfo[]): UserCognitoInfo[] {
    if (!users) {
        return [];
    }

    return users
        .filter(user => {
            // only normal users
            return user.groups.includes('Registered') || user.groups.includes('Admin');
        })
        .sort((a, b) => {
            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();

            if (nameA < nameB) {
                return -1;
            }

            if (nameA > nameB) {
                return 1;
            }

            return 0;
        });
}

const useStyles = makeStyles(styles);

const SessionHistory = () => {
    const classes = useStyles();
    // use me for current user, or cognito userId for other users
    const [userId, setUserId] = useState('me');
    const { sessions, isLoading: isSessionsLoading } = useListSessions(userId);
    const [startDateFilter, setStartDateFilter] = useState<any>(null);
    const [endDateFilter, setEndDateFilter] = useState<any>(null);
    const { users, isLoading: isUsersLoading } = useListUsers();
    const { user: currentUser } = useUser();

    const filteredSessions = filterSessionsByDate(sessions, startDateFilter, endDateFilter);
    const totalProcedures = filteredSessions.length;
    const totalTimeWatched = filteredSessions.reduce((total, session) => {
        return total + (session.timeWatched || 0);
    }, 0);
    const totalTimeWatchedFormatted = convertMinutesToHoursAndMinutes(totalTimeWatched);
    const filteredUsers = useMemo(() => filterAndSortedUsers(users), [users]);

    const columns: GridColDef[] = [
        {
            field: 'procedureId',
            headerName: 'ID',
            width: 80,
            sortable: true,
        },
        {
            field: 'facilityName',
            headerName: 'Facility',
            width: 300,
            sortable: true,
        },
        {
            field: 'doctorName',
            headerName: 'Doctor',
            width: 300,
            sortable: true,
        },
        {
            field: 'title',
            headerName: 'Title',
            width: 300,
            sortable: true,
        },
        {
            field: 'patientInitials',
            headerName: 'PI',
            width: 80,
            sortable: true,
        },
        {
            field: 'startTime',
            headerName: 'Date/Time',
            width: 200,
            sortable: true,
            valueFormatter: params => {
                return new Date(params.value).toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                });
            },
        },
        {
            field: 'timeWatched',
            headerName: 'Time Watched',
            width: 150,
            sortable: true,
            valueFormatter: params => {
                return convertMinutesToHoursAndMinutes(params.value);
            },
        },
    ];

    return (
        <div className={classes.root}>
            <Panel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container direction="row" justifyContent="center" alignItems="flex-start" spacing={1}>
                        <RequireAdmin>
                            <Box width="100px">User:</Box>
                            <Autocomplete
                                loading={isUsersLoading}
                                options={filteredUsers}
                                value={
                                    users.find(user => {
                                        if (userId === 'me') {
                                            return user.userId === currentUser?.attributes.sub;
                                        }

                                        return user.userId === userId;
                                    }) || null
                                }
                                getOptionLabel={option => {
                                    if (!option) {
                                        return '<unknown>';
                                    }
                                    return `${option.firstName} ${option.lastName} (${option.email})`;
                                }}
                                onChange={(_, option) => setUserId(option?.userId || '<unknown>')}
                                renderInput={params => <TextField {...params} label="User" variant="outlined" />}
                                sx={{ width: '300px', mr: '10px' }}
                            />
                        </RequireAdmin>
                        <Box width="100px">Date Filter:</Box>
                        <DatePicker
                            name="startDate"
                            label="Start Date"
                            value={startDateFilter}
                            onChange={newValue => setStartDateFilter(newValue)}
                            disableFuture
                            sx={{ width: '200px', mr: '10px' }}
                        />
                        <DatePicker
                            name="endDate"
                            label="End Date"
                            value={endDateFilter}
                            onChange={newValue => setEndDateFilter(newValue)}
                            disableFuture
                            sx={{ width: '200px', mr: '10px' }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => {
                                setStartDateFilter(null);
                                setEndDateFilter(null);
                            }}
                        >
                            Clear Date Filter
                        </Button>
                    </Grid>
                </LocalizationProvider>
                <Box width="100%">
                    <DataGrid
                        loading={isSessionsLoading}
                        actions={[]}
                        rows={filteredSessions}
                        getRowId={row => row.procedureId}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 20,
                                },
                            },
                        }}
                        pageSizeOptions={[10]}
                        slots={{ toolbar: GridToolbar }}
                        hideFooterSelectedRowCount={true}
                        className={classes.cellBorder}
                        sx={{ bgcolor: 'background.paper' }}
                    />
                </Box>
                <Box width="100%">
                    Total Procedures: <b>{totalProcedures}</b> Total Time Watched: <b>{totalTimeWatchedFormatted}</b>
                    <br />
                    Total from all items listed in the table, including all pages, and based on the filters applied.
                </Box>
            </Panel>
        </div>
    );
};

export default SessionHistory;
